<template>
  <div class="schedule-fleet-task-bulk">
    <b-modal
      id="modal-schedule-fleet-task-bulk"
      class="schedule-fleet-task__modal"
      :title="$t('delivery.action.scheduleFleetTask')"
      size="md"
      centered
      scrollable
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      @show="onShowDirect"
    >
      <div v-if="hasConfirmedMixedProductCategory">
        <McAlert withIcon class="w-100 mb-3" variant="warning">
          <template #label>
            <span
              ><b>{{ $t('global.alert.text.warningUpperCase') }}! </b
              >{{ $t('delivery.message.mixedProductCategoryOrEmptyDelivery') }}</span
            >
          </template>
        </McAlert>
      </div>
      <div class="card-body p-2">
        <p class="schedule-fleet-task__text">
          {{ $t('delivery.message.scheduleFleetTaskForDelivery') }}
          <strong>{{ selectedUpdateIds.length }}</strong
          >?
        </p>
        <p class="schedule-fleet-task__text" v-html="scheduledStatusFleetTaskCreatedMessage"></p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex mx-0">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.action.cancel')"
            :disabled="isLoading"
            @click="handleCancelModal(cancel)"
          />
          <McButton
            variant="primary"
            :text="$t('delivery.action.scheduleFleetTask')"
            :disabled="isLoading"
            @click="handleSubmit"
          />
        </div>
      </template>
    </b-modal>

    <b-modal
      id="modal-schedule-fleet-task-bulk-transit"
      class="schedule-fleet-task__modal"
      :title="$t('delivery.action.scheduleFleetTasktTransit')"
      size="md"
      centered
      scrollable
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      @show="onShowTransit"
    >
      <div v-if="hasConfirmedMixedProductCategory">
        <McAlert withIcon class="w-100 mb-3" variant="warning">
          <template #label>
            <span
              ><b>{{ $t('global.alert.text.warningUpperCase') }}! </b
              >{{ $t('delivery.message.mixedProductCategoryOrEmptyDelivery') }}</span
            >
          </template>
        </McAlert>
      </div>
      <div class="card-body p-2">
        <FormulateForm ref="formRef" v-model="formValues" class="mc-form mb-2" :errors="formErrors">
          <FormulateInput
            :ref="`${transitLocationFields.name}-ref`"
            v-bind="{ ...transitLocationFields }"
            :fullObject.sync="transitAddressFullObject"
            class="p-0"
            validation-behavior="submit"
            :help="transitLocationFields?.validation?.includes('required')"
          />
        </FormulateForm>

        <p class="schedule-fleet-task__text">
          {{ $t('delivery.message.scheduleFleetTaskForDelivery') }}
          <strong>{{ selectedUpdateIds.length }}</strong>
          {{ '?' }}
        </p>
        <p class="schedule-fleet-task__text" v-html="scheduledStatusFleetTaskCreatedMessage"></p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex mx-0">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.action.cancel')"
            :disabled="isLoading"
            @click="handleCancelModal(cancel)"
          />
          <McButton
            variant="primary"
            :text="$t('delivery.action.scheduleFleetTask')"
            :disabled="isLoading"
            @click="handleSubmitTransit"
          />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import * as FleetTask from '@/services/rest-api/lib/fleet-task/fleet-task'
import * as Address from '@/services/rest-api/lib/delivery/address'
import * as Delivery from '@/services/rest-api/lib/delivery/delivery-order'
import * as Load from '@/services/rest-api/lib/delivery/load'
import { errorCodeToastMap } from '@/router/views/fleet-task/lib/constants/error-code-mappings'
import { mapGetters } from 'vuex'

export default {
  components: {
    McButton,
    McAlert,
  },
  props: {
    selectedUpdateIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      formValues: {},
      formErrors: {},
      transitAddressFullObject: {},
      hasConfirmedMixedProductCategory: false,
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
    scheduledStatusFleetTaskCreatedMessage() {
      return this.$t('delivery.message.deliveryStatusScheduledAndFleetTaskCreated', {
        status: `<strong>${this.$t('delivery.status.scheduled')}</strong>`,
      })
    },
    transitLocationFields() {
      const transitField = {
        name: 'transit_address_id',
        showKey: 'origin_address',
        type: 'select-resourceful-v2',
        label: this.$t('delivery.fields.transitLocation'),
        optionsGetter: Address.getAddressTransit,
        optionLabelFn: ({ item }) => item.name,
        placeholder: this.$t('delivery.action.selectTransitLocation'),
        validation: 'required',
      }
      return transitField
    },
    hasSingleProductCategoryAccess() {
      return this.hasAccessAll(['fleet-task-single-category-deliveries:read'])
    },
  },
  watch: {
    'formValues.transit_address_id': {
      handler(newValue) {
        if (newValue) {
          this.formErrors = {}
        }
      },
    },
  },
  methods: {
    async onShowDirect() {
      this.hasConfirmedMixedProductCategory = await this.checkMixProductCategory()
    },
    async onShowTransit() {
      this.hasConfirmedMixedProductCategory = await this.checkMixProductCategory()
    },
    async handleSubmit() {
      if (this.isLoading) return

      this.isLoading = true
      let fleetTaskId = null
      this.hasConfirmedMixedProductCategory = await this.checkMixProductCategory()

      const ids = this.selectedUpdateIds.map((item) => item.id)
      const payload = {
        delivery_order_ids: ids,
      }
      if (this.hasSingleProductCategoryAccess && this.hasConfirmedMixedProductCategory === false) {
        const productCategoryId = await this.getProductCategoryIdFromFirstLoad()
        if (productCategoryId) {
          payload.product_category_id = productCategoryId
        }
      }

      const res = await FleetTask.store(payload)
      if (res.errors) {
        this.$mcToast.error(this.$t('delivery.alert.failedToCreateFleetTask'))
        this.isLoading = false
        this.$bvModal.hide('modal-schedule-fleet-task-bulk')

        return
      }

      fleetTaskId = res.data.id
      this.$store.dispatch('fleetTask/setSelectedDos', [...ids])
      this.isLoading = false

      this.$router.push({
        name: 'Fleet Task Detail',
        params: { id: fleetTaskId },
        query: { isSetNavsource: false },
      })
    },
    async handleSubmitTransit() {
      if (this.isLoading) return

      this.isLoading = true
      let fleetTaskId = null
      this.hasConfirmedMixedProductCategory = await this.checkMixProductCategory()

      if (!this.formValues.transit_address_id) {
        this.formErrors = {
          transit_address_id: [this.$t('global.validation.required')],
        }
        this.isLoading = false
        return
      }

      const ids = this.selectedUpdateIds.map((item) => item.id)
      const payload = {
        transit_delivery_orders: [
          {
            delivery_order_ids: ids,
            transit_address_id: this.formValues.transit_address_id,
          },
        ],
      }

      if (this.hasSingleProductCategoryAccess && this.hasConfirmedMixedProductCategory === false) {
        const productCategoryId = await this.getProductCategoryIdFromFirstLoad()
        if (productCategoryId) {
          payload.product_category_id = productCategoryId
        }
      }

      const res = await FleetTask.store(payload)
      let toastMessage = 'Gagal membuat penugasan'
      if (res.errors) {
        const issue = res.errors_v2.issues.find((issue) => errorCodeToastMap[issue.code])
        if (issue) toastMessage = errorCodeToastMap[issue.code]
        this.$mcToast.error(toastMessage)
        this.isLoading = false
        this.$bvModal.hide('modal-schedule-fleet-task-bulk-transit')
        return
      }

      fleetTaskId = res.data.id
      this.$store.dispatch('fleetTask/setSelectedDos', [...ids])
      this.isLoading = false

      this.$router.push({
        name: 'Fleet Task Detail',
        params: { id: fleetTaskId },
        query: { isSetNavsource: false },
      })
    },
    async checkMixProductCategory() {
      if (!this.hasSingleProductCategoryAccess) return false

      const ids = this.selectedUpdateIds.map((item) => item.id)

      const res = await Delivery.validateDo({
        validation_type: 'HAS_MIXED_PRODUCT_CATEGORY',
        ids: ids,
      })

      return res?.result
    },
    async getProductCategoryIdFromFirstLoad() {
      const ids = this.selectedUpdateIds.map((item) => item.id)
      const getLoads = Load.get(ids[0])
      const res = await getLoads({
        limit: 1,
        page: 1,
      })

      if (res?.errors) return null

      const firstLoad = res?.data?.paginated_result?.[0]

      return (
        firstLoad?.product?.product_type?.product_category?.id ||
        firstLoad?.product_type?.product_category?.id ||
        null
      )
    },
    handleCancel() {
      this.$emit('handleCancel')
    },
    handleCancelModal(cancelFn) {
      cancelFn()
      this.handleCancel()
    },
  },
}
</script>
